import { Component, ViewChild, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

import { BulkActionFormComponent } from '../MonitoringBulkActionDialog/bulk-action-form.component';
import { TagModel } from '../../../../Common/Tags/models/tag.model';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { MonitoringBulkActionTypeEnum } from '../../enums/monitoring-bulk-action-type.enum';
import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { TopicsService } from '../../../Core/services/topics.service';
import { MentionSearchListItemModel } from '../../models/mention-search-list-item.model';
import { AutocompleteComponent } from '../../../../Common/Forms/components/Autocomplete/autocomplete.component';



export enum TaggingMethodSelectionEnum {
    chooseTags,
    basedOnTaggingTopics
}

// the actionValue for this form
export interface AddTagBulkActionModel {
    method: TaggingMethodSelectionEnum;
    tag: TagModel;
    taggingSavedSearchIds: number[];
}

@Component({
    selector: 'agpr-bulk-action-form-tag',
    templateUrl: './bulk-action-form-tag.component.html',
    styleUrls: ['./bulk-action-form-tag.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BulkActionFormTagComponent),
            multi: true
        }],
    standalone: false
})
export class BulkActionFormTagComponent extends BulkActionFormComponent implements ControlValueAccessor {
    public readonly taggingMethodSelectionEnum = TaggingMethodSelectionEnum;
    public selectedTaggingMethod: TaggingMethodSelectionEnum = TaggingMethodSelectionEnum.chooseTags;

    public selectedTag: TagModel;

    public tagList$: Observable<TagModel[]>;

    public taggingTopicChips: { label: string; topic: MentionSearchListItemModel }[] = [];
    public taggingTopicList$: Observable<MentionSearchListItemModel[]>;
    public selectedTaggingTopics: MentionSearchListItemModel[] = [];

    @ViewChild('taggingTopicsAutocomplete') taggingTopicsAutocomplete: AutocompleteComponent;

    customOptions: AutocompleteCustomOption[] = [
        {
            label: 'Tags.createTag',
            action: (searchString: string) => {
                const tagModel = new TagModel();
                tagModel.name = searchString;

                this.tagsService.createTag(tagModel).pipe(
                    switchMap((id) => this.tagsService.getTagById(id))
                ).subscribe(tag => {
                    this.selectedTag = tag;
                    this.emitModelChange();
                });

            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    constructor(
        public tagsService: TagsService,
        private topicsService: TopicsService
    ) {
        super();
        this.tagList$ = tagsService.all$;
        this.taggingTopicList$ = this.topicsService.all$.pipe(map(topics => topics.filter(item => !!item?.applyTagIds?.length)));
    }

    writeValue(tag: TagModel): void {
        this.selectedTag = tag;
    }

    public changeTaggingMethod(taggingMethod: TaggingMethodSelectionEnum): void {
        if (taggingMethod === TaggingMethodSelectionEnum.chooseTags) {
            this.action.buttonLabel = 'Monitoring.bulk.action.addTag.button.chooseTag';
            this.action.type = MonitoringBulkActionTypeEnum.addTags;

        } else if (taggingMethod === TaggingMethodSelectionEnum.basedOnTaggingTopics) {
            this.action.buttonLabel = 'Monitoring.bulk.action.addTag.button.chooseTags';
            this.action.type = MonitoringBulkActionTypeEnum.addTagsFromTaggingTopics;
            this.action.disabledButtonTooltip = '';
            this.selectedTag = undefined;
        }
        this.emitModelChange();
    }

    selectTaggingTopic(topic: MentionSearchListItemModel) {
        if (this.taggingTopicChips.findIndex(x => x.topic.name === topic.name) === -1) {
            this.taggingTopicChips.push({
                label: topic.name,
                topic: topic
            });
            this.taggingTopicChips = cloneDeep(this.taggingTopicChips);
        };

        this.taggingTopicsAutocomplete.clearInput();
        this.emitModelChange();
    }

    removeTaggingTopicChip(index: number): void {
        const removeTopic: MentionSearchListItemModel = this.taggingTopicChips[index].topic;
        this.taggingTopicChips.splice(index, 1);
        this.selectedTaggingTopics = this.selectedTaggingTopics.filter((topic) => topic.name !== removeTopic.name);
        this.taggingTopicChips = cloneDeep(this.taggingTopicChips);

        this.emitModelChange();
    }

    emitModelChange(): void {
        let tag = this.selectedTag;
        let taggingSavedSearchIds = this.selectedTaggingTopics?.map(x => x.id);

        if (this.selectedTaggingMethod === TaggingMethodSelectionEnum.chooseTags) {
            taggingSavedSearchIds = [];
        } else {
            tag = null;
        }

        this.onModelChange({
            method: this.selectedTaggingMethod,
            tag,
            taggingSavedSearchIds
        });
    }

}
